import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  BarChart, 
  LineChart, 
  Line, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  DollarSign, 
  Users, 
  ShoppingCart, 
  Download,
  Plus,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Star,
  Repeat,
  UserCheck,
  Activity
} from 'lucide-react';
import { auth } from '@/lib/firebase/config';
import { 
  getSellerEarnings, 
  getTopWorkflows,
  getCustomerInsights,
  type WorkflowWithSales
} from '@/lib/firebase/workflow-service';
import { useToast } from "@/components/ui/use-toast";

interface DashboardData {
  earnings: {
    totalEarnings: number;
    totalSales: number;
    salesByDay: { date: string; sales: number; earnings: number }[];
  };
  topWorkflows: WorkflowWithSales[];
  customerInsights: {
    totalCustomers: number;
    repeatCustomers: number;
    averageRating: number;
    totalReviews: number;
  };
}

export default function SellerDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    earnings: {
      totalEarnings: 0,
      totalSales: 0,
      salesByDay: []
    },
    topWorkflows: [],
    customerInsights: {
      totalCustomers: 0,
      repeatCustomers: 0,
      averageRating: 0,
      totalReviews: 0
    }
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }

      setIsLoading(true);

      try {
        const sellerId = auth.currentUser.uid;
        
        // Fetch all data in parallel
        const [earnings, topWorkflows, customerInsights] = await Promise.all([
          getSellerEarnings(sellerId, selectedPeriod),
          getTopWorkflows(sellerId),
          getCustomerInsights(sellerId)
        ]);

        setDashboardData({
          earnings,
          topWorkflows,
          customerInsights
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast({
          title: "Error",
          description: "Failed to load dashboard data. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate, selectedPeriod, toast]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Seller Dashboard</h1>
          <p className="text-muted-foreground">Track your sales, workflows, and customer insights</p>
        </div>
        <Button onClick={() => navigate('/seller/upload')}>
          <Plus className="mr-2 h-4 w-4" />
          Upload Workflow
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(dashboardData.earnings.totalEarnings)}</div>
            <p className="text-xs text-muted-foreground">Lifetime earnings after fees</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Sales</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.earnings.totalSales}</div>
            <p className="text-xs text-muted-foreground">Workflows sold</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.customerInsights.totalCustomers}</div>
            <div className="flex items-center text-xs text-muted-foreground">
              <Repeat className="h-3 w-3 mr-1" />
              {dashboardData.customerInsights.repeatCustomers} repeat customers
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dashboardData.customerInsights.averageRating.toFixed(1)}
            </div>
            <p className="text-xs text-muted-foreground">
              From {dashboardData.customerInsights.totalReviews} reviews
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Sales Chart */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Sales Over Time</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant={selectedPeriod === 'week' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setSelectedPeriod('week')}
                  >
                    Week
                  </Button>
                  <Button 
                    variant={selectedPeriod === 'month' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setSelectedPeriod('month')}
                  >
                    Month
                  </Button>
                  <Button 
                    variant={selectedPeriod === 'year' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setSelectedPeriod('year')}
                  >
                    Year
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                {isLoading ? (
                  <div className="h-full flex items-center justify-center">
                    Loading chart data...
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={dashboardData.earnings.salesByDay}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return date.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          });
                        }}
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => formatCurrency(value)}
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right"
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        formatter={(value: any, name) => {
                          if (name === 'earnings') return formatCurrency(value);
                          return value;
                        }}
                        labelFormatter={(label) => {
                          const date = new Date(label);
                          return date.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          });
                        }}
                      />
                      <Legend />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="sales" 
                        name="Sales" 
                        stroke="#8884d8" 
                        activeDot={{ r: 8 }} 
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="earnings" 
                        name="Earnings" 
                        stroke="#82ca9d" 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Workflows */}
          <Card>
            <CardHeader>
              <CardTitle>Top Performing Workflows</CardTitle>
              <CardDescription>Your best-selling workflows</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-40 flex items-center justify-center">
                  Loading top workflows...
                </div>
              ) : dashboardData.topWorkflows.length > 0 ? (
                <div className="space-y-8">
                  {dashboardData.topWorkflows.map((workflow) => (
                    <div key={workflow.id} className="flex items-center">
                      <div className="space-y-1 flex-1">
                        <p className="text-sm font-medium leading-none">
                          {workflow.title}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(workflow.price)} · {workflow.totalSales} sales
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">
                          {formatCurrency(workflow.totalEarnings)}
                        </p>
                        <div className="flex items-center justify-end gap-1 text-sm">
                          {workflow.totalSales > 5 ? (
                            <TrendingUp className="h-3 w-3 text-green-500" />
                          ) : (
                            <TrendingDown className="h-3 w-3 text-amber-500" />
                          )}
                          <span className={workflow.totalSales > 5 ? "text-green-500" : "text-amber-500"}>
                            {workflow.totalSales > 5 ? "Popular" : "Low sales"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <AlertTriangle className="h-8 w-8 text-muted-foreground mb-2" />
                  <h3 className="text-lg font-medium">No workflow sales yet</h3>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Once you start selling workflows, you'll see your top performers here.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workflows" className="space-y-4">
          {/* Workflow Management Tab Content */}
          <Card>
            <CardHeader>
              <CardTitle>Workflow Performance</CardTitle>
              <CardDescription>Detailed view of all your workflows</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-40 flex items-center justify-center">
                  Loading workflows...
                </div>
              ) : dashboardData.topWorkflows.length > 0 ? (
                <div className="space-y-6">
                  {dashboardData.topWorkflows.map((workflow) => (
                    <div key={workflow.id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-medium">{workflow.title}</h3>
                          <p className="text-sm text-muted-foreground">{workflow.description}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/workflow/${workflow.id}`)}
                        >
                          View Details
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Price</p>
                          <p className="font-medium">{formatCurrency(workflow.price)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Total Sales</p>
                          <p className="font-medium">{workflow.totalSales}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Total Earnings</p>
                          <p className="font-medium">{formatCurrency(workflow.totalEarnings)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <AlertTriangle className="h-8 w-8 text-muted-foreground mb-2" />
                  <h3 className="text-lg font-medium">No workflows found</h3>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Start by uploading your first workflow to begin selling.
                  </p>
                  <Button 
                    className="mt-4"
                    onClick={() => navigate('/seller/upload')}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Upload Workflow
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customers" className="space-y-4">
          {/* Customer Insights Tab Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Card>
              <CardHeader>
                <CardTitle>Customer Overview</CardTitle>
                <CardDescription>Key metrics about your customer base</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Customers</p>
                      <p className="text-2xl font-bold">{dashboardData.customerInsights.totalCustomers}</p>
                    </div>
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Repeat Customers</p>
                      <p className="text-2xl font-bold">{dashboardData.customerInsights.repeatCustomers}</p>
                    </div>
                    <Repeat className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Customer Satisfaction</p>
                      <div className="flex items-center gap-2">
                        <p className="text-2xl font-bold">
                          {dashboardData.customerInsights.averageRating.toFixed(1)}
                        </p>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < Math.floor(dashboardData.customerInsights.averageRating)
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <Star className="h-8 w-8 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Customer Retention</CardTitle>
                <CardDescription>Analysis of customer loyalty</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Repeat Purchase Rate</p>
                    <div className="relative pt-1">
                      <div className="flex mb-2 items-center justify-between">
                        <div>
                          <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full bg-green-200 text-green-600">
                            {((dashboardData.customerInsights.repeatCustomers / 
                              dashboardData.customerInsights.totalCustomers) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-green-200">
                        <div
                          style={{ width: `${(dashboardData.customerInsights.repeatCustomers / 
                            dashboardData.customerInsights.totalCustomers) * 100}%` }}
                          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500"
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Review Rate</p>
                    <div className="relative pt-1">
                      <div className="flex mb-2 items-center justify-between">
                        <div>
                          <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full bg-blue-200 text-blue-600">
                            {((dashboardData.customerInsights.totalReviews / 
                              dashboardData.customerInsights.totalCustomers) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-200">
                        <div
                          style={{ width: `${(dashboardData.customerInsights.totalReviews / 
                            dashboardData.customerInsights.totalCustomers) * 100}%` }}
                          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          {/* Analytics Tab Content */}
          <Card>
            <CardHeader>
              <CardTitle>Revenue Analytics</CardTitle>
              <CardDescription>Detailed breakdown of your earnings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                {isLoading ? (
                  <div className="h-full flex items-center justify-center">
                    Loading analytics...
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dashboardData.earnings.salesByDay}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return date.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          });
                        }}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => formatCurrency(value)}
                      />
                      <Tooltip
                        formatter={(value: any) => formatCurrency(value)}
                        labelFormatter={(label) => {
                          const date = new Date(label);
                          return date.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          });
                        }}
                      />
                      <Legend />
                      <Bar dataKey="earnings" name="Revenue" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 